import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  configurarCarnetPublico,
  obtenerCarnetPublico,
  obtenerCarnetVacunacion,
  obtenerCuentaActual,
  obtenerPerfilTutorLocal,
  TipoItem,
  type CarnetVacunacionDto,
} from "../api";
import { formatearFecha } from "../formato";
import AvatarMascota from "../componentes/AvatarMascota";
import CodigoQR from "../componentes/CodigoQR";

export default function CarnetVacunacion() {
  const { mascotaID, token } = useParams<{ mascotaID?: string; token?: string }>();
  const esModoPublico = Boolean(token);
  const qc = useQueryClient();

  const [modalQR, setModalQR] = useState(false);
  const [modalConfig, setModalConfig] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [filtroDesparasitacion, setFiltroDesparasitacion] = useState<"todas" | "internas" | "externas">("todas");

  // Query para modo privado (con sesión)
  const queryPrivado = useQuery({
    queryKey: ["carnet", mascotaID],
    queryFn: () => obtenerCarnetVacunacion(mascotaID!),
    enabled: !esModoPublico && Boolean(mascotaID),
  });

  // Query para modo público (sin sesión)
  const queryPublico = useQuery({
    queryKey: ["carnet-publico", token],
    queryFn: () => obtenerCarnetPublico(token!),
    enabled: esModoPublico && Boolean(token),
  });

  // Cuenta para datos de tutor local en modo privado
  const cuenta = useQuery({
    queryKey: ["cuenta"],
    queryFn: obtenerCuentaActual,
    enabled: !esModoPublico,
  });

  const queryActiva = esModoPublico ? queryPublico : queryPrivado;
  const carnet: CarnetVacunacionDto | undefined = queryActiva.data;

  // Mutación para configurar token público
  const mutacionConfig = useMutation({
    mutationFn: (req: { habilitado?: boolean; regenerarToken?: boolean }) =>
      configurarCarnetPublico(mascotaID!, req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carnet", mascotaID] });
    },
  });

  if (queryActiva.isPending) {
    return (
      <div className="carnet-contenedor-centrado" style={{ padding: "3rem 1rem", textAlign: "center" }}>
        <div className="spinner-carga" style={{ fontSize: "2rem", marginBottom: "1rem" }}>🐾</div>
        <p className="muted">Cargando carnet de vacunación y registro sanitario…</p>
      </div>
    );
  }

  if (queryActiva.error || !carnet) {
    return (
      <div className="carnet-contenedor-centrado" style={{ padding: "3rem 1rem", textAlign: "center", maxWidth: "600px", margin: "0 auto" }}>
        <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>🔒</div>
        <h2>Carnet no disponible</h2>
        <p className="muted" style={{ margin: "0.5rem 0 1.5rem" }}>
          {queryActiva.error instanceof Error
            ? queryActiva.error.message
            : "El enlace no es válido, ha caducado o el acceso público está deshabilitado por el tutor."}
        </p>
        <Link to="/" className="boton-primario">
          Ir al inicio
        </Link>
      </div>
    );
  }

  const { mascota, tutor, vacunas, desparasitaciones, refuerzosPendientes, estadoGeneral } = carnet;

  // Enriquecer datos del tutor con perfil local si estamos en modo privado
  const perfilLocal = !esModoPublico && cuenta.data
    ? obtenerPerfilTutorLocal(cuenta.data.usuarioID, cuenta.data.nombre, cuenta.data.email)
    : null;

  const tutorNombre = perfilLocal?.nombreCompleto || tutor.nombreCompleto || "Tutor Responsable";
  const tutorTelefono = perfilLocal?.telefono || tutor.telefono;
  const tutorTelefonoEmergencia = perfilLocal?.telefonoEmergencia || tutor.telefonoEmergencia;
  const tutorEmail = perfilLocal?.email || tutor.email;
  const tutorDireccion = [perfilLocal?.direccion || tutor.direccion, perfilLocal?.comuna || tutor.comuna]
    .filter(Boolean)
    .join(", ");
  const tutorNotas = perfilLocal?.notasContacto || tutor.notasContacto;

  // Filtrado de desparasitaciones
  const desparasitacionesFiltradas = desparasitaciones.filter((d) => {
    if (filtroDesparasitacion === "internas") return d.tipo === TipoItem.AntiparasitarioInterno;
    if (filtroDesparasitacion === "externas") return d.tipo === TipoItem.AntiparasitarioExterno;
    return true;
  });

  // URL pública a compartir
  const host = window.location.origin;
  const urlPublicaReal = carnet.tokenPublico
    ? `${host}/carnet/${carnet.tokenPublico}`
    : carnet.urlPublica || "";

  // Generador de texto para WhatsApp
  const handleCompartirWhatsApp = () => {
    const textoLineas = [
      "🐾 *CARNET DE VACUNACIÓN Y REGISTRO SANITARIO*",
      `🐶 *Paciente:* ${mascota.nombre} (${mascota.especie}${mascota.raza ? ` · ${mascota.raza}` : ""})`,
      mascota.numeroChip ? `🏷️ *Microchip:* ${mascota.numeroChip}` : "",
      mascota.pesoKg != null ? `⚖️ *Peso:* ${mascota.pesoKg.toFixed(1)} kg` : "",
      mascota.alergias ? `⚠️ *Alergias:* ${mascota.alergias}` : "⚠️ *Alergias:* Ninguna registrada",
      "",
      `💉 *Vacunación:* ${vacunas.length > 0 ? `${vacunas.length} vacuna(s) registrada(s)` : "Sin registros"}`,
      vacunas[0] ? `• Última: ${vacunas[0].nombre} (${formatearFecha(vacunas[0].fecha)})` : "",
      "",
      `🧴 *Desparasitación:* ${desparasitaciones.length > 0 ? `${desparasitaciones.length} aplicada(s)` : "Sin registros"}`,
      desparasitaciones[0] ? `• Última: ${desparasitaciones[0].nombre} (${formatearFecha(desparasitaciones[0].fecha)})` : "",
      "",
      "🚨 *Contacto de Emergencia:*",
      `👤 Tutor: ${tutorNombre}`,
      tutorTelefono ? `📞 Tel: ${tutorTelefono}` : "",
      tutorTelefonoEmergencia ? `🚨 Urgencias: ${tutorTelefonoEmergencia}` : "",
      "",
      urlPublicaReal ? `🌐 *Ver carnet digital oficial y certificados:* ${urlPublicaReal}` : "",
    ].filter(Boolean);

    const mensajeEncoded = encodeURIComponent(textoLineas.join("\n"));
    window.open(`https://wa.me/?text=${mensajeEncoded}`, "_blank");
  };

  // Descarga e impresión formal en formato PDF
  const handleDescargarPDF = () => {
    const tituloAnterior = document.title;
    const fecha = new Date().toISOString().split("T")[0];
    const nombreLimpio = mascota.nombre.trim().replace(/[^a-zA-Z0-9_-]/g, "_");
    document.title = `Carnet_Vacunacion_${nombreLimpio}_${fecha}`;
    window.print();
    setTimeout(() => {
      document.title = tituloAnterior;
    }, 1500);
  };

  const handleCopiarEnlace = async () => {
    if (!urlPublicaReal) return;
    try {
      await navigator.clipboard.writeText(urlPublicaReal);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 3000);
    } catch {
      // Fallback
    }
  };

  return (
    <div className={`carnet-pagina-wrapper ${esModoPublico ? "carnet-modo-publico" : ""}`}>
      {/* Navegación y migas (solo en modo privado) */}
      {!esModoPublico && (
        <div className="sin-impresion carnet-barra-superior">
          <p className="migas" style={{ margin: 0 }}>
            <Link to={`/mascotas/${mascota.mascotaID}`}>← Volver a {mascota.nombre}</Link>
          </p>
          <div className="carnet-acciones-privadas">
            <button
              type="button"
              className="boton-chico"
              onClick={() => setModalConfig(true)}
              title="Configurar enlace público y privacidad"
            >
              ⚙️ Enlace público {carnet.compartirPublicoHabilitado ? "🟢 Activo" : "🔴 Desactivado"}
            </button>
          </div>
        </div>
      )}

      {/* Banner de barra pública para modo invitado */}
      {esModoPublico && (
        <header className="sin-impresion carnet-cabecera-publica">
          <div className="carnet-publica-marca">
            <span className="marca-icono">🐾</span>
            <div>
              <strong>Fichas Veterinarias</strong>
              <span className="muted" style={{ fontSize: "0.75rem", display: "block" }}>
                Ficha Sanitaria Oficial y Carnet Digital
              </span>
            </div>
          </div>
          <div className="carnet-publica-acciones">
            {tutorTelefono && (
              <a
                href={`tel:${tutorTelefono.replace(/\s+/g, "")}`}
                className="boton-chico boton-primario"
                style={{ textDecoration: "none" }}
              >
                📞 Contactar Tutor
              </a>
            )}
            <button
              type="button"
              className="boton-chico"
              onClick={handleDescargarPDF}
              title="Descargar o imprimir carnet oficial en PDF"
            >
              📄 Descargar PDF
            </button>
          </div>
        </header>
      )}

      {/* DOCUMENTO OFICIAL DEL CARNET (Imprimible y visual) */}
      <main className="carnet-documento-hoja">
        {/* Cabecera Oficial del Certificado */}
        <header className="carnet-documento-cabecera">
          <div className="carnet-sello-oficial">
            <span className="sello-icono">🛡️</span>
            <div className="sello-textos">
              <span className="sello-titulo">REGISTRO CLÍNICO VETERINARIO</span>
              <span className="sello-subtitulo">Carnet de Vacunación & Control Sanitario</span>
            </div>
          </div>

          <div className="carnet-estado-badge-wrap">
            <span
              className={`carnet-estado-badge ${
                estadoGeneral === "AlDia"
                  ? "estado-al-dia"
                  : estadoGeneral === "PorVencer"
                    ? "estado-por-vencer"
                    : estadoGeneral === "Vencido"
                      ? "estado-vencido"
                      : "estado-sin-registros"
              }`}
            >
              {estadoGeneral === "AlDia" && "✅ Inmunización al Día"}
              {estadoGeneral === "PorVencer" && "⚠️ Refuerzos por Vencer"}
              {estadoGeneral === "Vencido" && "🚨 Vacunación Vencida"}
              {estadoGeneral === "SinRegistros" && "📋 Registro Sanitario"}
            </span>
            {carnet.tokenPublico && (
              <span className="carnet-folio-codigo muted">
                FOLIO: {carnet.tokenPublico.slice(0, 10).toUpperCase()}
              </span>
            )}
          </div>
        </header>

        {/* Botones de acción flotantes / rápidos (sin imprimir) */}
        <div className="sin-impresion carnet-barra-botones-accion">
          <button
            type="button"
            className="boton-primario"
            onClick={handleDescargarPDF}
            title="Descargar certificado / carnet oficial en PDF"
          >
            📄 Descargar en PDF / Imprimir
          </button>
          <button
            type="button"
            className="boton"
            onClick={handleCompartirWhatsApp}
            title="Enviar ficha médica estructurada por WhatsApp"
          >
            💬 Enviar por WhatsApp
          </button>
          <button
            type="button"
            className="boton"
            onClick={() => setModalQR(true)}
            title="Ver código QR para escanear en teléfono"
          >
            📱 Código QR
          </button>
          <button
            type="button"
            className="boton"
            onClick={handleCopiarEnlace}
            title="Copiar enlace del carnet al portapapeles"
          >
            {copiado ? "✓ ¡Enlace copiado!" : "🔗 Copiar enlace"}
          </button>
        </div>

        {/* Grilla Superior: Identificación del Paciente y Tutor de Emergencia */}
        <div className="carnet-grilla-identificacion">
          {/* Tarjeta del Paciente */}
          <section className="carnet-tarjeta-seccion carnet-tarjeta-paciente">
            <div className="carnet-paciente-cabecera">
              <AvatarMascota
                nombre={mascota.nombre}
                especie={mascota.especie}
                fotoPerfilUrl={mascota.fotoPerfilUrl}
                tamano="lg"
                mascotaID={mascota.mascotaID}
              />
              <div className="carnet-paciente-datos-principales">
                <h1 className="carnet-paciente-nombre">{mascota.nombre}</h1>
                <p className="carnet-paciente-subtitulo">
                  {mascota.especie} {mascota.raza ? `· ${mascota.raza}` : ""} {mascota.color ? `· ${mascota.color}` : ""}
                </p>
                <div className="carnet-paciente-chips">
                  <span className="etiqueta-carnet">
                    {mascota.sexo === 1 ? "♂ Macho" : mascota.sexo === 2 ? "♀ Hembra" : "Sexo N/E"}
                  </span>
                  {mascota.edad && <span className="etiqueta-carnet">🎂 {mascota.edad}</span>}
                  {mascota.pesoKg != null && (
                    <span className="etiqueta-carnet">⚖️ {mascota.pesoKg.toFixed(1)} kg</span>
                  )}
                  {mascota.esterilizada !== null && (
                    <span className="etiqueta-carnet">
                      {mascota.esterilizada ? "✂️ Esterilizado(a)" : "✂️ Sin esterilizar"}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Número de Microchip destacado */}
            {mascota.numeroChip ? (
              <div className="carnet-chip-banner">
                <div className="chip-icono">🏷️</div>
                <div className="chip-info">
                  <span className="chip-etiqueta">MICROCHIP NACIONAL OFICIAL</span>
                  <span className="chip-numero">{mascota.numeroChip}</span>
                </div>
              </div>
            ) : (
              <div className="carnet-chip-banner chip-ausente">
                <span className="muted">Sin número de microchip registrado</span>
              </div>
            )}

            {/* Alerta de Alergias u Observaciones */}
            {mascota.alergias && (
              <div className="carnet-alerta-medica">
                <strong>⚠️ ALERGIAS Y PRECAUCIONES MÉDICAS:</strong>
                <p style={{ margin: "0.2rem 0 0" }}>{mascota.alergias}</p>
              </div>
            )}
          </section>

          {/* Tarjeta del Tutor y Contacto de Emergencia */}
          <section className="carnet-tarjeta-seccion carnet-tarjeta-tutor">
            <h2 className="carnet-seccion-titulo">🚨 Contacto de Emergencia y Tutor</h2>
            
            <div className="carnet-tutor-datos">
              <div className="carnet-dato-fila">
                <span className="carnet-dato-label">Tutor / Responsable:</span>
                <span className="carnet-dato-valor">👤 {tutorNombre}</span>
              </div>

              {tutor.rut && (
                <div className="carnet-dato-fila">
                  <span className="carnet-dato-label">RUT / Identificación:</span>
                  <span className="carnet-dato-valor">{tutor.rut}</span>
                </div>
              )}

              {tutorTelefono && (
                <div className="carnet-dato-fila">
                  <span className="carnet-dato-label">Teléfono de Contacto:</span>
                  <span className="carnet-dato-valor">
                    <a href={`tel:${tutorTelefono.replace(/\s+/g, "")}`} className="enlace-telefono">
                      📞 {tutorTelefono}
                    </a>
                  </span>
                </div>
              )}

              {tutorTelefonoEmergencia && (
                <div className="carnet-dato-fila carnet-emergencia-destacado">
                  <span className="carnet-dato-label">Teléfono Urgencias 24h:</span>
                  <span className="carnet-dato-valor" style={{ color: "var(--crit)", fontWeight: 700 }}>
                    <a href={`tel:${tutorTelefonoEmergencia.replace(/\s+/g, "")}`} className="enlace-emergencia">
                      🚨 {tutorTelefonoEmergencia}
                    </a>
                  </span>
                </div>
              )}

              {tutorEmail && (
                <div className="carnet-dato-fila">
                  <span className="carnet-dato-label">Correo Electrónico:</span>
                  <span className="carnet-dato-valor">📧 {tutorEmail}</span>
                </div>
              )}

              {tutorDireccion && (
                <div className="carnet-dato-fila">
                  <span className="carnet-dato-label">Dirección / Comuna:</span>
                  <span className="carnet-dato-valor">📍 {tutorDireccion}</span>
                </div>
              )}

              {tutorNotas && (
                <div className="carnet-dato-fila">
                  <span className="carnet-dato-label">Instrucciones de Cuidado:</span>
                  <span className="carnet-dato-valor muted" style={{ fontStyle: "italic" }}>
                    📌 {tutorNotas}
                  </span>
                </div>
              )}
            </div>

            {/* Botones de contacto rápido para emergencias en móvil */}
            {tutorTelefono && (
              <div className="sin-impresion carnet-tutor-botones-urgencia">
                <a
                  href={`tel:${tutorTelefono.replace(/\s+/g, "")}`}
                  className="boton-chico boton-primario"
                  style={{ textDecoration: "none", flex: 1, textAlign: "center" }}
                >
                  📞 Llamar al tutor
                </a>
                <a
                  href={`https://wa.me/${tutorTelefono.replace(/[^0-9]/g, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="boton-chico etiqueta-whatsapp"
                  style={{ textDecoration: "none", flex: 1, textAlign: "center" }}
                >
                  💬 WhatsApp
                </a>
              </div>
            )}
          </section>
        </div>

        {/* SECCIÓN 1: HISTORIAL OFICIAL DE VACUNACIONES */}
        <section className="carnet-seccion-bloque">
          <div className="carnet-seccion-cabecera-fila">
            <div className="carnet-seccion-icono-titulo">
              <span className="seccion-icono">💉</span>
              <div>
                <h2>Registro Oficial de Vacunaciones</h2>
                <p className="muted" style={{ margin: 0, fontSize: "0.85rem" }}>
                  Inmunizaciones aplicadas, antígenos, números de lote y firmas de validación clínica.
                </p>
              </div>
            </div>
            <span className="etiqueta neutra">
              {vacunas.length} {vacunas.length === 1 ? "vacuna registrada" : "vacunas registradas"}
            </span>
          </div>

          {vacunas.length === 0 ? (
            <div className="carnet-vacio-cuadro">
              <p className="muted">No hay registros de vacunas aplicadas en el historial clínico.</p>
            </div>
          ) : (
            <div className="carnet-tabla-contenedor">
              <table className="carnet-tabla">
                <thead>
                  <tr>
                    <th style={{ width: "15%" }}>Fecha</th>
                    <th style={{ width: "30%" }}>Vacuna / Antígenos</th>
                    <th style={{ width: "15%" }}>Dosis / Vía</th>
                    <th style={{ width: "15%" }}>N° Lote</th>
                    <th style={{ width: "25%" }}>Clínica / Profesional</th>
                  </tr>
                </thead>
                <tbody>
                  {vacunas.map((v) => (
                    <tr key={v.visitaItemID} className="carnet-fila-vacuna">
                      <td className="carnet-td-fecha">
                        <strong>📅 {formatearFecha(v.fecha)}</strong>
                      </td>
                      <td>
                        <div className="carnet-item-nombre-destacado">
                          <strong>{v.nombre}</strong>
                          {v.numeroDosisEnSerie && (
                            <span className="etiqueta-dosis-serie">
                              Dosis {v.numeroDosisEnSerie}
                            </span>
                          )}
                        </div>
                        {v.notas && <span className="carnet-item-notas-chicas">{v.notas}</span>}
                      </td>
                      <td>
                        <span className="muted">
                          {[v.dosis, v.via].filter(Boolean).join(" · ") || "—"}
                        </span>
                      </td>
                      <td>
                        {v.lote ? (
                          <span className="carnet-lote-badge" title="Lote oficial del biológico">
                            {v.lote}
                          </span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td>
                        <div className="carnet-profesional-sello">
                          {v.profesionalNombre && <span>👨‍⚕️ {v.profesionalNombre}</span>}
                          {v.clinicaNombre && <span className="muted">🏥 {v.clinicaNombre}</span>}
                          <span className="sello-validador-texto">✓ Validación Clínica</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* SECCIÓN 2: CONTROL DE DESPARASITACIONES */}
        <section className="carnet-seccion-bloque">
          <div className="carnet-seccion-cabecera-fila">
            <div className="carnet-seccion-icono-titulo">
              <span className="seccion-icono">🧴</span>
              <div>
                <h2>Control de Desparasitaciones</h2>
                <p className="muted" style={{ margin: 0, fontSize: "0.85rem" }}>
                  Tratamientos antiparasitarios internos (comprimidos/suspensiones) y externos (pipetas/collares/masticables).
                </p>
              </div>
            </div>

            <div className="sin-impresion carnet-filtros-pills">
              <button
                type="button"
                className={`filtro-pill ${filtroDesparasitacion === "todas" ? "activo" : ""}`}
                onClick={() => setFiltroDesparasitacion("todas")}
              >
                Todas ({desparasitaciones.length})
              </button>
              <button
                type="button"
                className={`filtro-pill ${filtroDesparasitacion === "internas" ? "activo" : ""}`}
                onClick={() => setFiltroDesparasitacion("internas")}
              >
                🪱 Internas ({desparasitaciones.filter((d) => d.tipo === TipoItem.AntiparasitarioInterno).length})
              </button>
              <button
                type="button"
                className={`filtro-pill ${filtroDesparasitacion === "externas" ? "activo" : ""}`}
                onClick={() => setFiltroDesparasitacion("externas")}
              >
                🧴 Externas ({desparasitaciones.filter((d) => d.tipo === TipoItem.AntiparasitarioExterno).length})
              </button>
            </div>
          </div>

          {desparasitacionesFiltradas.length === 0 ? (
            <div className="carnet-vacio-cuadro">
              <p className="muted">No hay registros de desparasitaciones en esta categoría.</p>
            </div>
          ) : (
            <div className="carnet-tabla-contenedor">
              <table className="carnet-tabla">
                <thead>
                  <tr>
                    <th style={{ width: "15%" }}>Fecha</th>
                    <th style={{ width: "15%" }}>Tipo</th>
                    <th style={{ width: "30%" }}>Producto Administrado</th>
                    <th style={{ width: "15%" }}>Dosis / Vía</th>
                    <th style={{ width: "25%" }}>Clínica / Profesional</th>
                  </tr>
                </thead>
                <tbody>
                  {desparasitacionesFiltradas.map((d) => (
                    <tr key={d.visitaItemID}>
                      <td className="carnet-td-fecha">
                        <strong>📅 {formatearFecha(d.fecha)}</strong>
                      </td>
                      <td>
                        <span
                          className={`etiqueta ${
                            d.tipo === TipoItem.AntiparasitarioInterno ? "al-dia" : "neutra"
                          }`}
                        >
                          {d.tipo === TipoItem.AntiparasitarioInterno ? "🪱 Interno" : "🧴 Externo"}
                        </span>
                      </td>
                      <td>
                        <strong>{d.nombre}</strong>
                        {d.lote && <span className="carnet-item-notas-chicas">Lote: {d.lote}</span>}
                        {d.notas && <span className="carnet-item-notas-chicas">{d.notas}</span>}
                      </td>
                      <td>
                        <span className="muted">
                          {[d.dosis, d.via].filter(Boolean).join(" · ") || "—"}
                        </span>
                      </td>
                      <td>
                        <div className="carnet-profesional-sello">
                          {d.profesionalNombre && <span>👨‍⚕️ {d.profesionalNombre}</span>}
                          {d.clinicaNombre && <span className="muted">🏥 {d.clinicaNombre}</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* SECCIÓN 3: PRÓXIMOS REFUERZOS Y CONTROLES */}
        {refuerzosPendientes.length > 0 && (
          <section className="carnet-seccion-bloque">
            <div className="carnet-seccion-cabecera-fila">
              <div className="carnet-seccion-icono-titulo">
                <span className="seccion-icono">⏰</span>
                <div>
                  <h2>Calendario de Próximos Refuerzos y Revacunación</h2>
                  <p className="muted" style={{ margin: 0, fontSize: "0.85rem" }}>
                    Fechas estimadas según protocolos de periodicidad y vigencia inmunológica.
                  </p>
                </div>
              </div>
            </div>

            <div className="carnet-refuerzos-grid">
              {refuerzosPendientes.map((r) => {
                const claseSemaforo =
                  r.estadoSemaforo === "Vencido"
                    ? "vencido"
                    : r.estadoSemaforo === "PorVencer"
                      ? "por-vencer"
                      : "al-dia";

                return (
                  <article key={r.recordatorioID} className={`carnet-refuerzo-card ${claseSemaforo}`}>
                    <div className="refuerzo-cab">
                      <span className="refuerzo-titulo">{r.titulo}</span>
                      <span className={`etiqueta ${claseSemaforo}`}>
                        {r.estadoSemaforo === "Vencido" && "Vencido"}
                        {r.estadoSemaforo === "PorVencer" && "Por vencer"}
                        {r.estadoSemaforo === "AlDia" && "Al día"}
                      </span>
                    </div>
                    <div className="refuerzo-fecha-wrap">
                      <span className="refuerzo-fecha">📅 {formatearFecha(r.fechaProgramada)}</span>
                      <span className="refuerzo-dias muted">
                        {r.diasRestantes < 0
                          ? `(Vencido hace ${Math.abs(r.diasRestantes)} días)`
                          : r.diasRestantes === 0
                            ? "(¡Vence hoy!)"
                            : `(En ${r.diasRestantes} días)`}
                      </span>
                    </div>
                    {!esModoPublico && (
                      <div className="refuerzo-acciones no-print" style={{ marginTop: "0.5rem" }}>
                        <Link
                          to={`/mascotas/${mascota.mascotaID}/visitas/nueva?productoNombre=${encodeURIComponent(r.titulo)}&motivo=${encodeURIComponent(`Aplicación de ${r.titulo}`)}`}
                          className="boton-chico boton-primario"
                          title={`Registrar aplicación para ${r.titulo}`}
                        >
                          ⚡ Registrar aplicación
                        </Link>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </section>
        )}

        {/* Pie de Certificado Oficial */}
        <footer className="carnet-documento-pie">
          <div className="pie-aviso-legal">
            <p>
              Documento emitido y validado digitalmente a través del sistema <strong>Fichas Veterinarias</strong>.
              Este carnet certifica los procedimientos clínicos y biológicos administrados al paciente por profesionales autorizados.
            </p>
          </div>
          <div className="pie-qr-estampa">
            {urlPublicaReal && (
              <div className="pie-qr-contenedor">
                <CodigoQR valor={urlPublicaReal} tamano={80} />
                <span className="pie-qr-texto">Escanear para verificar</span>
              </div>
            )}
          </div>
        </footer>
      </main>

      {/* MODAL DE CÓDIGO QR */}
      {modalQR && (
        <div className="modal-backdrop sin-impresion" onClick={() => setModalQR(false)}>
          <div className="modal-dialog panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "450px", textAlign: "center" }}>
            <header className="modal-cabecera">
              <h2 style={{ margin: 0 }}>Código QR del Carnet</h2>
              <button type="button" className="boton-chico" onClick={() => setModalQR(false)}>
                ✕
              </button>
            </header>
            <div style={{ padding: "1rem 0" }}>
              <p className="muted" style={{ marginBottom: "1rem" }}>
                Escanea este código con cualquier cámara móvil para abrir el carnet y la ficha de emergencia sin necesidad de iniciar sesión.
              </p>
              <CodigoQR valor={urlPublicaReal} titulo={`Carnet de ${mascota.nombre}`} tamano={220} />
              <div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem", justifyContent: "center" }}>
                <button type="button" className="boton" onClick={handleCopiarEnlace}>
                  {copiado ? "✓ ¡Copiado!" : "📋 Copiar enlace directo"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE CONFIGURACIÓN PÚBLICA (Solo en modo privado) */}
      {modalConfig && (
        <div className="modal-backdrop sin-impresion" onClick={() => setModalConfig(false)}>
          <div className="modal-dialog panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "520px" }}>
            <header className="modal-cabecera">
              <h2 style={{ margin: 0 }}>Configuración de Ficha Pública</h2>
              <button type="button" className="boton-chico" onClick={() => setModalConfig(false)}>
                ✕
              </button>
            </header>
            <div style={{ padding: "1rem 0" }}>
              <p className="muted" style={{ fontSize: "0.9rem" }}>
                El enlace público permite a guarderías, paseadores, veterinarias de turno y en caso de extravío consultar el carnet de vacunación y datos de emergencia sin necesidad de crear una cuenta.
              </p>

              <div className="campo" style={{ marginTop: "1rem" }}>
                <label className="checkbox-linea">
                  <input
                    type="checkbox"
                    checked={carnet.compartirPublicoHabilitado}
                    disabled={mutacionConfig.isPending}
                    onChange={(e) =>
                      mutacionConfig.mutate({ habilitado: e.target.checked })
                    }
                  />
                  <span>
                    <strong>Habilitar acceso público al carnet mediante enlace y QR</strong>
                  </span>
                </label>
              </div>

              {carnet.compartirPublicoHabilitado && (
                <div style={{ marginTop: "1rem", padding: "0.75rem", backgroundColor: "var(--sunken)", borderRadius: "0.5rem" }}>
                  <span className="muted" style={{ fontSize: "0.75rem", textTransform: "uppercase" }}>
                    Enlace público actual:
                  </span>
                  <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.3rem" }}>
                    <input
                      type="text"
                      readOnly
                      value={urlPublicaReal}
                      style={{ fontSize: "0.85rem", flex: 1 }}
                    />
                    <button type="button" className="boton-chico" onClick={handleCopiarEnlace}>
                      {copiado ? "✓" : "Copiar"}
                    </button>
                  </div>

                  <div style={{ marginTop: "1rem", borderTop: "1px solid var(--borde)", paddingTop: "0.75rem" }}>
                    <span className="muted" style={{ fontSize: "0.8rem", display: "block", marginBottom: "0.4rem" }}>
                      Si sospechas que alguien no autorizado tiene el enlace, puedes generar un código nuevo:
                    </span>
                    <button
                      type="button"
                      className="boton-chico"
                      disabled={mutacionConfig.isPending}
                      onClick={() => {
                        if (confirm("¿Regenerar el enlace público? El código QR y enlaces anteriores dejarán de funcionar de inmediato.")) {
                          mutacionConfig.mutate({ regenerarToken: true });
                        }
                      }}
                    >
                      🔄 Regenerar enlace y código QR
                    </button>
                  </div>
                </div>
              )}

              <footer className="modal-acciones" style={{ marginTop: "1.5rem" }}>
                <button type="button" className="boton-primario" onClick={() => setModalConfig(false)}>
                  Listo
                </button>
              </footer>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
